<script>
    import {onMount} from 'svelte';
    import {PATH_PREFIX} from '../../apiUrls.js';
    import {get} from '../../utils.js';
    import {sgettext} from '../../i18n.js';
    import {submit} from '../Form/utils.js';
    import {validateForm} from '../Form/utils.js';

    import CheckBox from '../CheckBox.svelte';
    import ComboSelect from '../ComboSelect/ComboSelect.svelte';
    import Errors from '../Errors.svelte';
    import Modal from '../Modal.svelte';
    import TextInput from '../TextInput.svelte';
    import yup, {object, number, string} from 'yup';

    export let formModalId = 'd2BranchConfigurationForm';
    export let onSubmit = (data) => data;
    export let requireReload = false;
    export let branchId = null;
    export let editConfig = null;

    let branchSettingUrl = `${PATH_PREFIX}/sw/instruction/d2/branch-setting/`; 

    let title = sgettext('$$CONFIGURE_BRANCH$$');
    let selectedOptions = getDefaultConfig();
    let isValid = true;
    let sending = false;
    let loading = false;
    let formErrors = null;
    let edit = false;

    $: branchOptions = [];

    function getDefaultConfig() {
        return {
            branch_id: branchId,
            pdf_template_id: null,
            document_repository: null,
            enabled : false,
            maintenance_mode: false,
            fieldValidations: getDefaultConfigFieldIsValid(),
        };
    }

    $: if (editConfig) {
        selectedOptions = getEditConfig();
        fetchResults(editConfig.branch_id);
        editConfig = null;
        edit = true;
    }

    function getEditConfig() {
        return {
            branch_id: editConfig.branch_id,
            pdf_template_id: editConfig.instructions_pdf_templates[0],
            document_repository: editConfig.d2_repository,
            enabled : editConfig.enabled,
            maintenance_mode: editConfig.maintenance_mode,
            edit: true,
            fieldValidations: getDefaultConfigFieldIsValid(),
        };
    }

    function getDefaultConfigFieldIsValid() {
        return {
            pdf_template_id: {
                isValid: true,
                errors: '',
            },
            document_repository: {
                isValid: true,
                errors: '',
            },
        };
    }

    let rowSchema = object().shape({
        pdf_template_id: number()
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
        document_repository: string()
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
    });

    async function submitForm() {
        sending = true;
        const method = 'POST';
        let url = `${branchSettingUrl}`;

        try {
            let payload = selectedOptions;
            await handleFormValidation();
            if (isValid) {
                const response = await submit(url, method, payload);
                if (!response.ok) {formErrors = sgettext('$$FAILED_SAVE_NEW_CONFIGURATION$$')};
                requireReload = true;
                handleConfigClose();
            }
        } catch (error) {
            formErrors = error?.message || (Object.keys(error || {}).length ? error : sgettext('$$FAILED_SAVE_NEW_CONFIGURATION$$'));
        }
        sending = false;
    }

    async function handleFormValidation() {
        try {
            await validateForm(rowSchema, selectedOptions);
            isValid = true;
        } catch (err) {
            isValid = false;
            if (err.inner) {
                err.inner.forEach((er) => {
                    selectedOptions.fieldValidations[er.path].isValid = false;
                    selectedOptions.fieldValidations[er.path].errors = er.message;
                });
            }
        }}

    function handleConfigSave() {
        submitForm();
    }

    function handleConfigClose() {
        jQuery(`#${formModalId}`).modal('hide');
        selectedOptions = getDefaultConfig();
        isValid = true;
        loading = false;
        formErrors = null;
        editConfig = null;
        edit = false;
        fetchResults(branchId);
    }

    async function fetchResults(branchId) {
        if (!branchId) {console.error("branchId is null or undefined"); return;}
        loading = true;
        var url = `${branchSettingUrl}?branch_id=${branchId}`;
        const resp = await get(url);
        const json = await resp.json();
        if (json) {
            branchOptions = json;
            branchOptions.d2_repositories = branchOptions.d2_repositories.map(repo => ({id: repo[0], name: repo[1]}));
        }
        loading = false;
    }

    onMount(() => {
        fetchResults(branchId);
    });

</script>

<Modal
  id={formModalId}
  {title}
  backdrop={'static'}
  onCancel={handleConfigClose}
  onSave={handleConfigSave}
>
  <div slot="body">
    {#if loading}
      <div class="text-center">
        <i class="fas fa-spinner fa-spin" />
      </div>
    {:else}
      <form>
        {#if formErrors}
          <Errors errors={formErrors} />
        {/if}
        <TextInput
          id={`d2_branch_config_branch`}
          label={sgettext('$$BRANCH_NAME$$')}
          key={'full_name'}
          value={branchOptions.full_name}
          object={branchOptions}
          multiline={true}
          readonly={true}
        />
        <br />

        <ComboSelect
          id={`d2_branch_config_repository`}
          label={sgettext('$$D2_REPOSITORY$$')}
          tooltip={sgettext('$$DOCUMENTUM_REPOSITORY$$')}
          key={'document_repository'}
          options={branchOptions.d2_repositories}
          selected={selectedOptions.document_repository}
          bind:value={selectedOptions.document_repository}
          bind:isValid={selectedOptions.fieldValidations.document_repository
            .isValid}
          bind:errors={selectedOptions.fieldValidations.document_repository
            .errors}
          schema={rowSchema}
        />
        <br />
        <ComboSelect
          id={`d2_branch_config_pdf_template`}
          label={sgettext('$$PDF_TEMPLATE$$')}
          key={'pdf_template_id'}
          options={branchOptions.pdf_templates}
          selected={selectedOptions.pdf_template_id}
          bind:value={selectedOptions.pdf_template_id}
          bind:isValid={selectedOptions.fieldValidations.pdf_template_id
            .isValid}
          bind:errors={selectedOptions.fieldValidations.pdf_template_id.errors}
          schema={rowSchema}
        />
        <br />
        {#if edit}
          <CheckBox
            id="d2_branch_config_enabled"
            label={sgettext('$$ENABLED$$')}
            bind:checked={selectedOptions.enabled}
          />
          <CheckBox
            id="d2_branch_config_maintenace_mode"
            label={sgettext('$$MAINTENANCE_MODE$$')}
            bind:checked={selectedOptions.maintenance_mode}
          />
          <br />
          <div class:d-none={!selectedOptions.enabled} class="alert alert-info">
            {sgettext('$$NOTE_CHILD_BRANCHES_INHERIT_SETTINGS_PDF$$')}
          </div>
          <div
            class:d-none={!selectedOptions.maintenance_mode}
            class="alert alert-warning"
          >
            {sgettext('$$NOTE_ADMIN_D2_MAINTENANCE_MODE$$')}
          </div>
        {/if}
      </form>
    {/if}
  </div>
</Modal>
